import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingAppEditor from 'benchling-apps/BenchlingAppEditor';
import {BenchlingAppUiBlockType} from 'graphql-client/__generated/graphqlGlobalTypes';
import SelectorOptionInput from 'metadata/SelectorOptionInput';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import TextInput from 'ui-library/inputs/TextInput';
import MarkdownContent, {DANGEROUS_LEGACY_ALLOWED_URI_REGEXP} from 'ui-library/MarkdownContent';
import {InputChangeEvent} from 'util/Types';

import {FileUploadUiBlock} from './FileUpload';
import SearchInputMultiValueUiBlock from './SearchInput/SearchInputMultiValueUiBlock';
import {SelectorInputMultiValueUiBlock, SelectorInputUiBlock} from './SelectorInput';
import LabelWrapper from './shared/LabelWrapper';
import TableUiBlock from './tables/TableUiBlock';
import * as uiBlock from './UiBlockTypes';

interface ButtonUiBlockProps extends uiBlock.Button {
  onCanvasSubmit: (buttonId: string) => void;
}

export function ButtonUiBlock({
  userDefinedId,
  text,
  enabled,
  canvasEnabled,
  onCanvasSubmit,
}: ButtonUiBlockProps): JSX.Element {
  return (
    <Button
      buttonStyle={ButtonStyle.SECONDARY}
      size={ButtonSize.SMALL}
      text={text}
      disabled={!canvasEnabled || !enabled}
      onClick={() => {
        onCanvasSubmit(userDefinedId);
      }}
    />
  );
}

interface TextInputUiBlockProps extends uiBlock.TextInput {
  onChangeValue: (blockId: string, val: string) => void;
}

export function TextInputUiBlock({
  id,
  value,
  enabled,
  canvasEnabled,
  placeholder,
  onChangeValue,
  label,
  required,
}: TextInputUiBlockProps): JSX.Element {
  return (
    <div>
      <LabelWrapper label={label} required={required} />
      <TextInput
        disabled={!canvasEnabled || !enabled}
        onChange={(e: InputChangeEvent) => {
          onChangeValue(id, e.target.value);
        }}
        placeholder={placeholder ?? ''}
        value={value ?? ''}
      />
    </div>
  );
}

export function MarkdownUiBlock({value}: uiBlock.Markdown): JSX.Element {
  return (
    <div className={css(styles.mdInner)}>
      {/* Override security options because we used this component before the security options were added, and thus may have existing usages that rely on the dangerous behavior. */}
      <MarkdownContent
        content={value}
        dangerousSecurityOptions={{blockStyle: false, allowedUris: DANGEROUS_LEGACY_ALLOWED_URI_REGEXP}}
      />
    </div>
  );
}

interface SectionUiBlockProps extends uiBlock.Section {
  onCanvasSubmit: (buttonId: string) => void;
  onChangeValue: (blockId: string, val: string | null) => void;
  onTableUpdate: (plaintextTable: Array<Array<string>>, tableId: string, dataframeName: string) => void;
  canvasEnabled: boolean;
  canvasId: string;
}

export function SectionUiBlock({
  children,
  onCanvasSubmit,
  onChangeValue,
  onTableUpdate,
  canvasEnabled,
  canvasId,
}: SectionUiBlockProps): JSX.Element {
  return (
    <div className={classNames('u-spacing-top u-flex-display', css(styles.sectionUiBlock))}>
      {children.map((block, i) => (
        <div key={i} className={block.type === BenchlingAppUiBlockType.CHIP ? '' : 'u-small-spacing-right'}>
          <UiBlockWrapper
            block={block}
            onChangeValue={onChangeValue}
            onCanvasSubmit={onCanvasSubmit}
            onTableUpdate={onTableUpdate}
            canvasEnabled={canvasEnabled}
            canvasId={canvasId}
          />
        </div>
      ))}
    </div>
  );
}

export function ChipUiBlock({value}: uiBlock.Chip): JSX.Element {
  return <BenchChip id={value} size={ChipSize.SMALL} />;
}

interface SearchInputUiBlockProps extends uiBlock.SearchInput {
  onChangeValue: (blockId: string, val: string) => void;
}

export function SearchInputUiBlock({
  value,
  itemType,
  schemaId,
  onChangeValue,
  id,
  enabled,
  canvasEnabled,
  label,
  required,
  placeholder,
}: SearchInputUiBlockProps): JSX.Element {
  function onChangeDropdown(val: string): void {
    onChangeValue(id, val);
  }
  return (
    <div>
      <LabelWrapper label={label} required={required} />
      <BenchlingAppEditor
        name={id}
        type={itemType}
        subtype={null}
        {...(placeholder != null && {placeholder})}
        value={value}
        onChangeValue={onChangeDropdown}
        isEditable={enabled && canvasEnabled}
        schemaId={schemaId}
        // Following props don't have any function in this component but are needed for BenchlingAppEditor
        requiredConfig={false}
        dependencyValidation={{errors: [], subdependencyValidation: {}, outputSubdependencyValidation: {}}}
        updateDependencyConfigValidation={noop}
        elements={null}
        elementDefinition={null}
        onChangeElements={noop}
        updateArrayElementValidation={noop}
      />
    </div>
  );
}

interface DropdownUiBlockProps extends uiBlock.Dropdown {
  onChangeValue: (blockId: string, val: string | null) => void;
}

export function DropdownUiBlock({
  value,
  dropdownId,
  onChangeValue,
  id,
  enabled,
  canvasEnabled,
  label,
  required,
  placeholder,
}: DropdownUiBlockProps): JSX.Element {
  const defaultValue = value == null ? [] : [value];
  return (
    <div>
      <LabelWrapper label={label} required={required} />
      <SelectorOptionInput
        defaultValue={{schemaFieldSelectorOptionIds: defaultValue}}
        onChange={(val: {schemaFieldSelectorOptionIds: Array<string>; value: string}) => {
          if (val.schemaFieldSelectorOptionIds.length === 1) {
            onChangeValue(id, val.schemaFieldSelectorOptionIds[0]);
          } else {
            onChangeValue(id, null);
          }
        }}
        shouldAllowMultiple={false}
        schemaFieldSelectorId={dropdownId}
        disabled={!(enabled && canvasEnabled)}
        {...(placeholder != null && {placeholder})}
      />
    </div>
  );
}

interface DropdownMultiValueUiBlockProps extends uiBlock.DropdownMultiValue {
  onChangeValue: (blockId: string, val: Array<string> | null) => void;
}

export function DropdownMultiValueUiBlock({
  dropdownId,
  onChangeValue,
  id,
  enabled,
  canvasEnabled,
  label,
  blockValue,
  required,
  placeholder,
}: DropdownMultiValueUiBlockProps): JSX.Element {
  const defaultValue = blockValue?.values || [];
  return (
    <div>
      <LabelWrapper label={label} required={required} />
      <SelectorOptionInput
        defaultValue={{schemaFieldSelectorOptionIds: defaultValue}}
        onChange={(val: {schemaFieldSelectorOptionIds: Array<string>; value: string}) => {
          if (val.schemaFieldSelectorOptionIds.length > 0) {
            onChangeValue(id, val.schemaFieldSelectorOptionIds);
          } else {
            onChangeValue(id, null);
          }
        }}
        shouldAllowMultiple={true}
        schemaFieldSelectorId={dropdownId}
        disabled={!(enabled && canvasEnabled)}
        {...(placeholder != null && {placeholder})}
      />
    </div>
  );
}

interface UiBlockProps {
  canvasId: string;
  block: uiBlock.UiBlock;
  onChangeValue: (blockId: string, val: string | Array<string> | null) => void;
  onCanvasSubmit: (buttonId: string) => void;
  onTableUpdate: (plaintextTable: Array<Array<string>>, tableId: string, dataframeName: string) => void;
  canvasEnabled: boolean;
}

export function UiBlockWrapper({
  canvasId,
  block,
  onChangeValue,
  onCanvasSubmit,
  onTableUpdate,
  canvasEnabled,
}: UiBlockProps): JSX.Element {
  switch (block.type) {
    case BenchlingAppUiBlockType.TEXT_INPUT:
      return <TextInputUiBlock {...block} onChangeValue={onChangeValue} canvasEnabled={canvasEnabled} />;
    case BenchlingAppUiBlockType.BUTTON:
      return <ButtonUiBlock {...block} onCanvasSubmit={onCanvasSubmit} canvasEnabled={canvasEnabled} />;
    case BenchlingAppUiBlockType.MARKDOWN:
      return <MarkdownUiBlock {...block} />;
    case BenchlingAppUiBlockType.CHIP:
      return <ChipUiBlock {...block} />;
    case BenchlingAppUiBlockType.SEARCH_INPUT:
      return <SearchInputUiBlock {...block} onChangeValue={onChangeValue} canvasEnabled={canvasEnabled} />;
    case BenchlingAppUiBlockType.SELECTOR_INPUT:
      return <SelectorInputUiBlock {...block} onChangeValue={onChangeValue} canvasEnabled={canvasEnabled} />;
    case BenchlingAppUiBlockType.DROPDOWN:
      return <DropdownUiBlock {...block} onChangeValue={onChangeValue} canvasEnabled={canvasEnabled} />;
    case BenchlingAppUiBlockType.SECTION:
      return (
        <SectionUiBlock
          {...block}
          onChangeValue={onChangeValue}
          onCanvasSubmit={onCanvasSubmit}
          onTableUpdate={onTableUpdate}
          canvasEnabled={canvasEnabled}
          canvasId={canvasId}
        />
      );
    case BenchlingAppUiBlockType.TABLE:
      return (
        <TableUiBlock
          {...block}
          canvasId={canvasId}
          onTableUpdate={onTableUpdate}
          canvasEnabled={canvasEnabled}
        />
      );
    case BenchlingAppUiBlockType.DROPDOWN_MULTIVALUE:
      return (
        <DropdownMultiValueUiBlock {...block} onChangeValue={onChangeValue} canvasEnabled={canvasEnabled} />
      );
    case BenchlingAppUiBlockType.FILE_UPLOAD:
      return <FileUploadUiBlock {...block} onChangeValue={onChangeValue} canvasEnabled={canvasEnabled} />;
    case BenchlingAppUiBlockType.SELECTOR_INPUT_MULTIVALUE:
      return (
        <SelectorInputMultiValueUiBlock
          {...block}
          onChangeValue={onChangeValue}
          canvasEnabled={canvasEnabled}
        />
      );
    case BenchlingAppUiBlockType.SEARCH_INPUT_MULTIVALUE:
      return (
        <SearchInputMultiValueUiBlock
          {...block}
          onChangeValue={onChangeValue}
          canvasEnabled={canvasEnabled}
        />
      );
    default:
      assertAndContinue(false, `Invalid UI Block ${block}`);
      return <div />;
  }
}

const styles = StyleSheet.create({
  sectionUiBlock: {
    alignItems: 'center',
  },
  mdInner: {
    ':nth-child(1n) > div': {
      ':nth-child(1n) > p': {
        marginBottom: 0,
      },
    },
  },
});

export {
  SelectorInputMultiValueUiBlock,
  SearchInputMultiValueUiBlock,
  SelectorInputUiBlock,
  FileUploadUiBlock,
};
